import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  useListSlaPolicies,
  useCreateSlaPolicy,
  useUpdateSlaPolicy,
  useDeleteSlaPolicy,
  getListSlaPoliciesQueryKey,
  useGetBusinessHours,
  getGetBusinessHoursQueryKey,
  useUpdateBusinessHours,
} from "@workspace/api-client-react";
import type { SlaPolicy } from "@workspace/api-client-react";
import { useAuth } from "@/context/auth-context";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Clock, ShieldAlert } from "lucide-react";

// Phase 41 Plan 10 (Wave 7) — TCFG-02 Settings CRUD for sla_policy, plus the
// TCFG-07 business-hours round-trip card (this screen owns it per the plan's
// task action text). Only GENERATED hooks are consumed. Gated to
// admin+ops_lead (TPRM-05) — same pattern as categories-settings.tsx.
//
// `priority` has no counterpart in UpdateSlaPolicyBody -> immutable after
// create, shown read-only in the Edit dialog (same reasoning as
// task_category.type on the Categories screen).
//
// TCFG-06 (hard rule, from 41-CONTEXT.md): respondMinutes/resolveMinutes
// are RELATIVE durations edited here. The absolute freeze onto a task row
// happens at task-creation time (Phase 42) — editing a policy here never
// retroactively moves a deadline on a task already in flight. This screen
// only ever writes sla_policy's own template columns.

const CreateSchema = z.object({
  priority: z.string().min(1, "Priority is required"),
  respondMinutes: z.number().int().min(1, "Must be at least 1 minute"),
  resolveMinutes: z.number().int().min(1, "Must be at least 1 minute"),
  name: z.string().nullable().optional(),
});
const EditSchema = z.object({
  respondMinutes: z.number().int().min(1, "Must be at least 1 minute"),
  resolveMinutes: z.number().int().min(1, "Must be at least 1 minute"),
  name: z.string().nullable().optional(),
  active: z.boolean(),
});
type CreateValues = z.infer<typeof CreateSchema>;
type EditValues = z.infer<typeof EditSchema>;

const EMPTY_CREATE: CreateValues = { priority: "P3", respondMinutes: 30, resolveMinutes: 240, name: null };

function errStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}
function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}

function fmtMinutes(m: number): string {
  if (m < 60) return `${m}m`;
  if (m % 60 === 0) return `${m / 60}h`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function BusinessHoursCard({ canEdit }: { canEdit: boolean }) {
  const { data: hours, isLoading } = useGetBusinessHours(undefined, {
    query: { queryKey: getGetBusinessHoursQueryKey(), enabled: canEdit },
  });
  const updateHours = useUpdateBusinessHours();
  const queryClient = useQueryClient();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // Phase 42 (TBLD-05) — the ONE space-wide validator window. Lives here (not
  // per-item on the checklists-settings.tsx item modal) — this card already
  // owns the round-trip to the same GET/PATCH /task-config/business-hours
  // route the 42-06/42-08 contract extended with taskValidationWindowMinutes,
  // and this settings page is the natural home for a space-wide default next
  // to business hours, rather than adding a 7th field to every item.
  const [validationWindow, setValidationWindow] = useState("");

  useEffect(() => {
    if (hours) {
      setStart(hours.businessHoursStart ?? "");
      setEnd(hours.businessHoursEnd ?? "");
      setValidationWindow(hours.taskValidationWindowMinutes != null ? String(hours.taskValidationWindowMinutes) : "");
    }
  }, [hours]);

  const isDirty =
    hours &&
    (start !== (hours.businessHoursStart ?? "") ||
      end !== (hours.businessHoursEnd ?? "") ||
      validationWindow !== (hours.taskValidationWindowMinutes != null ? String(hours.taskValidationWindowMinutes) : ""));

  async function save() {
    try {
      await updateHours.mutateAsync({
        data: {
          businessHoursStart: start || null,
          businessHoursEnd: end || null,
          taskValidationWindowMinutes: validationWindow === "" ? null : Number(validationWindow),
        },
      });
      toast.success("Business hours saved.");
      queryClient.invalidateQueries({ queryKey: getGetBusinessHoursQueryKey() });
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to edit business hours.")
          : "Could not save business hours.",
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="h-4 w-4" /> Business hours &amp; validator window</CardTitle>
        <CardDescription>
          Business hours round-trip only this phase — the SLA clock that reads these to compute deadlines is Phase 42.
          The validator window below is live (Phase 42's dual clock reads it).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-10 w-64" />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label>Start</Label>
                <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="w-32" />
              </div>
              <div>
                <Label>End</Label>
                <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="w-32" />
              </div>
            </div>
            <div className="max-w-sm space-y-2 rounded-md border p-3">
              <Label htmlFor="task-validation-window">Validators must review within (minutes)</Label>
              <Input
                id="task-validation-window"
                type="number"
                min={1}
                placeholder="240"
                value={validationWindow}
                onChange={(e) => setValidationWindow(e.target.value)}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                ONE space-wide default for every item's validator clock — not set per item, not set per template.
                Blank uses the platform default (4h).
              </p>
            </div>
            <Button onClick={save} disabled={!isDirty || updateHours.isPending}>
              {updateHours.isPending ? "Saving…" : "Save hours"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function TaskSlaSettingsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "super_admin" || user?.role === "ops_lead";

  const queryClient = useQueryClient();
  // GET is open to any authenticated role server-side — `canEdit` below gates
  // whether this page renders the CRUD UI, not the fetch itself.
  const { data: policies, isLoading } = useListSlaPolicies();

  const [openCreate, setOpenCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<SlaPolicy | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<SlaPolicy | null>(null);

  const createMutation = useCreateSlaPolicy();
  const updateMutation = useUpdateSlaPolicy();
  const archiveMutation = useDeleteSlaPolicy();

  const createForm = useForm<CreateValues>({ resolver: zodResolver(CreateSchema), defaultValues: EMPTY_CREATE });
  const editForm = useForm<EditValues>({
    resolver: zodResolver(EditSchema),
    defaultValues: { respondMinutes: 30, resolveMinutes: 240, name: null, active: true },
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: getListSlaPoliciesQueryKey() });
  }
  function openEdit(p: SlaPolicy) {
    setEditTarget(p);
    editForm.reset({ respondMinutes: p.respondMinutes, resolveMinutes: p.resolveMinutes, name: p.name ?? null, active: p.active });
  }
  function closeDialogs() {
    setOpenCreate(false);
    setEditTarget(null);
    createForm.reset(EMPTY_CREATE);
  }

  async function onCreate(values: CreateValues) {
    try {
      await createMutation.mutateAsync({ data: values });
      toast.success(`Created SLA policy "${values.priority}".`);
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to add SLA policies.")
          : "Could not create the SLA policy.",
      );
    }
  }
  async function onEdit(values: EditValues) {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ id: editTarget.id, data: values });
      toast.success("SLA policy saved.");
      invalidate();
      closeDialogs();
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to edit SLA policies.")
          : "Could not save the SLA policy.",
      );
    }
  }
  async function onArchive() {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync({ id: archiveTarget.id });
      toast.success(`Archived "${archiveTarget.priority}".`);
      invalidate();
      setArchiveTarget(null);
    } catch (err) {
      const status = errStatus(err);
      toast.error(
        status === 403
          ? ((errData(err)?.message as string) ?? "You don't have permission to archive SLA policies.")
          : "Could not archive the SLA policy.",
      );
    }
  }

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Restricted</CardTitle>
          <CardDescription>SLA policy settings are limited to admin and ops lead roles.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">SLA Policies</h1>
            <p className="text-sm text-muted-foreground">
              Respond/resolve targets per priority. Editing here never moves a deadline already frozen onto a task in flight.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpenCreate(true)}>Add SLA Policy</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Policies {policies ? <span className="text-muted-foreground font-normal text-sm">({policies.length})</span> : null}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Priority</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Respond within</TableHead>
                <TableHead>Resolve within</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right w-[160px] pr-4">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>{[...Array(6)].map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>)}</TableRow>
                ))
              ) : policies?.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No SLA policies yet.</TableCell></TableRow>
              ) : (
                policies?.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell><Badge variant="secondary">{p.priority}</Badge></TableCell>
                    <TableCell>{p.name ?? "—"}</TableCell>
                    <TableCell className="text-sm">{fmtMinutes(p.respondMinutes)}</TableCell>
                    <TableCell className="text-sm">{fmtMinutes(p.resolveMinutes)}</TableCell>
                    <TableCell><Badge variant={p.active ? "default" : "outline"}>{p.active ? "Active" : "Archived"}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap pr-4">
                      <div className="flex justify-end items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>Edit</Button>
                        {p.active && (
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setArchiveTarget(p)}>
                            Archive
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <BusinessHoursCard canEdit={canEdit} />

      {/* Add / Edit dialog */}
      <Dialog open={openCreate || editTarget !== null} onOpenChange={(o) => !o && closeDialogs()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editTarget ? `Edit ${editTarget.priority} SLA policy` : "Add SLA Policy"}</DialogTitle></DialogHeader>
          {editTarget ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEdit)} className="space-y-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Priority: </span>
                  <span className="font-medium">{editTarget.priority}</span>
                  <span className="text-xs text-muted-foreground"> (fixed at creation)</span>
                </div>
                <FormField name="respondMinutes" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Respond within (minutes) <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input type="number" min={1} value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="resolveMinutes" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Resolve within (minutes) <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input type="number" min={1} value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="name" control={editForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input placeholder="Optional label" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="active" control={editForm.control} render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <FormLabel>Active</FormLabel>
                      <p className="text-xs text-muted-foreground">Off = archived; no longer assignable from a category.</p>
                    </div>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={updateMutation.isPending}>{updateMutation.isPending ? "Saving…" : "Save Policy"}</Button>
                </DialogFooter>
              </form>
            </Form>
          ) : (
            <Form {...createForm}>
              <form onSubmit={createForm.handleSubmit(onCreate)} className="space-y-3">
                <FormField name="priority" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input placeholder="P1 / P2 / P3 / P4" {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">Free text on the server; P1–P4 is the seeded convention.</p>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="respondMinutes" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Respond within (minutes) <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input type="number" min={1} value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="resolveMinutes" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Resolve within (minutes) <span className="text-destructive">*</span></FormLabel>
                    <FormControl><Input type="number" min={1} value={field.value} onChange={(e) => field.onChange(Number(e.target.value) || 0)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField name="name" control={createForm.control} render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input placeholder="Optional label" value={field.value ?? ""} onChange={(e) => field.onChange(e.target.value || null)} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={closeDialogs}>Discard changes</Button>
                  <Button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? "Creating…" : "Save Policy"}</Button>
                </DialogFooter>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiveTarget?.priority} policy?</AlertDialogTitle>
            <AlertDialogDescription>
              Sets it to inactive — categories referencing it keep the reference, but it stops appearing as
              assignable. Reversible from Edit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} disabled={archiveMutation.isPending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
